import { useEffect, useState } from 'react';
import type { BackupRecord } from '@panel/shared';
import { api } from '../lib/api.js';
import { bytes, fmtDate, relTime } from '../lib/format.js';
import { IconBackup, IconDownload, IconRefresh } from './Icons.js';

interface Props {
  Server_ID: number;
  canRestore: boolean;
}

export default function BackupsPanel({ Server_ID, canRestore }: Props) {
  const [backups, setBackups] = useState<BackupRecord[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = () => {
    api.get<{ backups: BackupRecord[] }>(`/api/backups?Server_ID=${Server_ID}`)
      .then((r) => setBackups(r.backups))
      .catch((err) => setError(err.message));
  };
  useEffect(reload, [Server_ID]);

  const download = async (key: string) => {
    try {
      const { url } = await api.get<{ url: string }>(`/api/backups/${encodeURIComponent(key)}/download-url`);
      window.open(url, '_blank');
    } catch (err) { alert((err as Error).message); }
  };

  const restore = async (key: string) => {
    if (!confirm(`Restore from ${key}?\n\nThe server will be stopped, current data moved aside, backup extracted, and server restarted. This is reversible — your current data is preserved in a sibling folder.`)) return;
    setBusy(true);
    try {
      await api.post('/api/backups/restore', { Server_ID, key });
      alert('Restore complete!');
      reload();
    } catch (err) { alert(`Restore failed: ${(err as Error).message}`); }
    finally { setBusy(false); }
  };

  return (
    <div className="card p-4">
      <header className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-fg">Backups</h3>
        {!canRestore && <span className="text-[11px] text-fg-dim">Restore requires admin</span>}
      </header>

      {error && (
        <div className="text-xs text-status-error mb-2 px-3 py-2 rounded-md bg-status-error/10 border border-status-error/30">
          {error}
        </div>
      )}

      {backups === null ? (
        <p className="text-sm text-fg-muted py-2">Loading…</p>
      ) : backups.length === 0 ? (
        <div className="py-6 flex flex-col items-center text-center gap-2">
          <div className="w-10 h-10 rounded-full bg-bg-raised border border-bg-border flex items-center justify-center text-fg-dim">
            <IconBackup />
          </div>
          <span className="text-sm text-fg-muted">No backups yet</span>
        </div>
      ) : (
        <div className="space-y-1 max-h-72 overflow-y-auto -mx-1 px-1">
          {backups.map((b) => (
            <div key={b.key} className="flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-bg-hover/60 text-sm group transition-colors">
              <span className={`dot shrink-0 ${b.status === 'complete' ? 'bg-status-online' : b.status === 'failed' ? 'bg-status-error' : 'bg-status-warn'}`} />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-fg truncate" title={b.key}>{fmtDate(b.started_at)}</div>
                <div className="text-[10px] text-fg-dim truncate">
                  {bytes(b.size_bytes)} · {relTime(b.started_at)}
                  {b.error && <span className="text-status-error"> · {b.error}</span>}
                </div>
              </div>
              <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                <button
                  onClick={() => download(b.key)}
                  className="btn-icon !w-7 !h-7"
                  title="Download"
                  aria-label="Download"
                >
                  <IconDownload className="w-3.5 h-3.5" />
                </button>
                {canRestore && b.status === 'complete' && (
                  <button
                    onClick={() => restore(b.key)}
                    disabled={busy}
                    className="btn-icon !w-7 !h-7 hover:!text-accent"
                    title="Restore"
                    aria-label="Restore"
                  >
                    <IconRefresh className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
